import { Slot } from "@radix-ui/react-slot"
import { LinkProps, Link as RouterLink } from "@tanstack/react-router"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"
import { cn } from "../utils"

const buttonVariants = cva(
  "cursor-pointer inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 [&_span]:line-clamp-1 [&_span]:truncate overflow-hidden shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90",
        destructive: "border border-destructive text-destructive hover:bg-destructive/10",
        outline:
          "border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/60",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "bg-background text-muted-foreground hover:text-primary hover:underline",
        sidebar: "bg-transparent hover:bg-secondary justify-start shadow-none p-3 border-0 w-full",
        gradient: "button-gradient-primary",
        testing: "button-gradient-testing",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-6 gap-2",
        xl: "h-12 rounded-md px-6 text-md gap-3",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    const content =
      typeof props.children === "string" ? <span>{props.children}</span> : props.children

    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props}>
        {content}
      </Comp>
    )
  },
)
Button.displayName = "Button"

const ButtonLink = React.forwardRef<
  HTMLAnchorElement,
  VariantProps<typeof buttonVariants> &
    Pick<React.ComponentProps<typeof Button>, "className" | "tabIndex" | "title" | "onClick"> &
    LinkProps
>(({ variant, size, disabled, className, onClick, ...linkProps }, ref) => {
  const content =
    typeof linkProps.children === "string" ? <span>{linkProps.children}</span> : linkProps.children

  return (
    <Button
      asChild={!disabled}
      variant={variant}
      size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      onClick={onClick}
      disabled={disabled}
    >
      {disabled ? (
        <>
          {typeof content === "function"
            ? content({ isActive: false })
            : content}
        </>
      ) : (
        <RouterLink ref={ref} {...linkProps}>
          {content}
        </RouterLink>
      )}
    </Button>
  )
}) as React.ForwardRefExoticComponent<
  VariantProps<typeof buttonVariants> &
    Pick<React.ComponentProps<typeof Button>, "className" | "tabIndex" | "title" | "onClick"> &
    LinkProps &
    React.RefAttributes<HTMLAnchorElement>
>
ButtonLink.displayName = "ButtonLink"

export { Button, ButtonLink, buttonVariants }
